import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Church, Plus, Edit, Trash2, Clock, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AdminCultos() {
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    day_of_week: '',
    time: '',
    description: '',
    location: '',
    is_active: true
  });

  const queryClient = useQueryClient();

  const { data: services = [], isLoading } = useQuery({
    queryKey: ['worshipServices'],
    queryFn: () => base44.entities.WorshipService.list()
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.WorshipService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipServices'] });
      toast.success('Culto criado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.WorshipService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipServices'] });
      toast.success('Culto atualizado!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.WorshipService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipServices'] });
      toast.success('Culto excluído!');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      day_of_week: '',
      time: '',
      description: '',
      location: '',
      is_active: true
    });
    setEditingService(null);
    setShowForm(false);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title || '',
      day_of_week: service.day_of_week || '',
      time: service.time || '',
      description: service.description || '',
      location: service.location || '',
      is_active: service.is_active ?? true
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.day_of_week || !formData.time) {
      toast.error('Título, dia e horário são obrigatórios.');
      return;
    }

    if (editingService) {
      updateMutation.mutate({ id: editingService.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const dayColors = {
    'Domingo': 'bg-indigo-500',
    'Segunda': 'bg-blue-500',
    'Terça': 'bg-emerald-500',
    'Quarta': 'bg-amber-500',
    'Quinta': 'bg-purple-500',
    'Sexta': 'bg-rose-500',
    'Sábado': 'bg-cyan-500',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-orange-500 to-amber-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Cultos Fixos" 
            subtitle="Programação semanal"
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {services.length === 0 ? (
          <EmptyState
            icon={Church}
            title="Nenhum culto cadastrado"
            description="Cadastre os cultos fixos da igreja."
            actionLabel="Adicionar Culto"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-xl shadow-sm overflow-hidden ${!service.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex">
                  <div className={`${dayColors[service.day_of_week] || 'bg-gray-500'} w-2`} />
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{service.title}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span>{service.day_of_week}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {service.time}
                          </span>
                        </div>
                        {service.location && (
                          <span className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                            <MapPin className="w-3 h-3" />
                            {service.location}
                          </span>
                        )}
                      </div>
                      {!service.is_active && (
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">Inativo</span>
                      )}
                    </div>

                    <div className="flex gap-2 mt-4 pt-3 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEdit(service)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200"
                        onClick={() => {
                          if (confirm('Excluir este culto?')) {
                            deleteMutation.mutate(service.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Editar Culto' : 'Novo Culto'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nome do Culto *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Culto de Celebração"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dia da Semana *</Label>
                <Select value={formData.day_of_week} onValueChange={(v) => setFormData({ ...formData, day_of_week: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Domingo">Domingo</SelectItem>
                    <SelectItem value="Segunda">Segunda</SelectItem>
                    <SelectItem value="Terça">Terça</SelectItem>
                    <SelectItem value="Quarta">Quarta</SelectItem>
                    <SelectItem value="Quinta">Quinta</SelectItem>
                    <SelectItem value="Sexta">Sexta</SelectItem>
                    <SelectItem value="Sábado">Sábado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Horário *</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Local</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ex: Templo Principal"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição do culto"
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Culto ativo</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingService ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}